import * as React from 'react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { getApiUrl } from 'api/useApi';
import { SavingState } from 'api/savingState';
import { Dialog, DialogFooter, DialogType, PrimaryButton } from '@fluentui/react';
import { ReactSketchCanvas, ReactSketchCanvasRef } from 'react-sketch-canvas';
import { Service, signService } from 'api/service';

const styles = mergeStyleSets({
    container: {
        backgroundColor: 'white',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999999,
    },
    iframe: {
        top: 0,
        left: 0,
        width: '100%',
        height: 'calc(100vh - 200px)',
    },
    signingWindow: {
        position: 'absolute',
        right: '10%',
        bottom: '10%',
        width: '40%',
        height: '30%',
    },
    buttonLine: {
        paddingTop: 8,
        paddingRight: 25,
        display: 'grid',
        gridTemplateColumns: '1fr 150px',
        zIndex: 9999999,
    },
});

const canvasStyles = {
    border: '0.0625rem solid #9c9c9c',
    borderRadius: '0.25rem',
};

interface ServiceSignProps {
    service: Service;
    onClose: (refetch: boolean) => void;
}

export const ServiceSign: React.FC<ServiceSignProps> = ({ service, onClose }) => {
    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [showSigningWindow, setShowSigningWindow] = React.useState(false);
    const [signed, setSigned] = React.useState(false);

    const canvas = React.createRef<ReactSketchCanvasRef>();

    const url =
        getApiUrl('service/pdf') + '?nr=' + service.nr + '&briefpapier=true' + (signed ? '&lastSigned=true' : '');

    const save = async () => {
        setSavingState(SavingState.saving);

        const signature = await canvas.current?.exportImage('png');

        if (signature) {
            const signatureBlob = await fetch(signature).then(async (res) => res.blob());

            const signatureFile: File = new File([signatureBlob], 'signature.png', {
                type: 'image/png',
            });

            await signService(service.nr, signatureFile);

            setShowSigningWindow(false);
            setSigned(true);
        }
    };

    return (
        <Dialog
            hidden={false}
            title={`Service ${service?.nr || ''} - ${service.kunde?.name1 || '...'} ${service.kunde?.name2 || ''} ${
                service.kunde?.nr ? '(' + service.kunde?.nr + ')' : ''
            }`}
            dialogContentProps={{
                type: DialogType.normal,
            }}
            modalProps={{
                isBlocking: true,
            }}
            minWidth="96%"
            maxWidth="96%"
            onDismiss={() => {
                onClose(false);
            }}
        >
            <iframe className={styles.iframe} title="service-sign-iframe" src={url} />

            <DialogFooter>
                {signed ? (
                    <PrimaryButton onClick={() => onClose(true)} text="Schließen" />
                ) : (
                    <PrimaryButton onClick={() => setShowSigningWindow(!showSigningWindow)} text="Unterschreiben" />
                )}
            </DialogFooter>

            {showSigningWindow && (
                <Dialog
                    hidden={false}
                    title={`Service ${service?.nr || ''} - ${service.kunde?.name1 || '...'} ${
                        service.kunde?.name2 || ''
                    } ${service.kunde?.nr ? '(' + service.kunde?.nr + ')' : ''}`}
                    dialogContentProps={{
                        type: DialogType.normal,
                    }}
                    modalProps={{
                        isBlocking: true,
                    }}
                    minWidth="50%"
                    maxWidth="50%"
                    onDismiss={() => {
                        setShowSigningWindow(false);
                    }}
                >
                    <ReactSketchCanvas
                        ref={canvas}
                        style={canvasStyles}
                        width="100%"
                        height="300px"
                        strokeWidth={4}
                        strokeColor="red"
                    />

                    <DialogFooter>
                        <PrimaryButton disabled={savingState === SavingState.saving} onClick={save} text="Fertig" />
                    </DialogFooter>
                </Dialog>
            )}
        </Dialog>
    );
};
